/**
 * Relay Client — Direct connection to Solidafy Query Relay
 *
 * Executes SQL and DSL queries directly against the relay service.
 * Credentials are fetched from the portal API and cached.
 */
import { normalizeSql } from './sql-utils';
import type { LlmResult } from '@/types/explorer';
import type { TraceTree } from '@/types/trace';
import { api } from '@/lib/api';
import { edenError } from '@solidafy/api-client';

let cachedCredentials: { relay_url: string; api_key: string } | null = null;
let credentialsFetchPromise: Promise<{ relay_url: string; api_key: string }> | null = null;

export type RelayQueryResult = {
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  error?: string;
  trace_tree?: TraceTree;
  llm?: LlmResult;
};

/** Shape of the raw JSON returned by the relay /query endpoint. */
type RelayRawResponse = {
  columns?: string[];
  rows?: Record<string, unknown>[];
  rowCount?: number;
  error?: string;
  trace_tree?: TraceTree;
  chart?: Record<string, unknown>;
  llm?: LlmResult;
};

function isDsl(code: string): boolean {
  const trimmed = code.trim();
  return (
    trimmed.startsWith('source(') ||
    trimmed.includes('.since(') ||
    trimmed.includes('.chart(') ||
    trimmed.includes('.llm(') ||
    trimmed.includes('.schema()') ||
    trimmed.includes('.trace_tree(')
  );
}

async function getRelayCredentials(): Promise<{ relay_url: string; api_key: string }> {
  if (cachedCredentials) {
    return cachedCredentials;
  }
  if (credentialsFetchPromise) {
    return credentialsFetchPromise;
  }

  credentialsFetchPromise = (async () => {
    const { data, error } = await api.api.v1.flight.credentials.get();

    if (error) {
      throw new Error(edenError(error, 'Failed to get relay credentials'));
    }

    cachedCredentials = { relay_url: data.relay_url, api_key: data.api_key };
    return cachedCredentials;
  })();

  try {
    return await credentialsFetchPromise;
  } finally {
    credentialsFetchPromise = null;
  }
}

export function clearRelayCredentials(): void {
  cachedCredentials = null;
  credentialsFetchPromise = null;
}

export async function executeRelayQuery(sql: string, options?: { signal?: AbortSignal }): Promise<RelayQueryResult> {
  const credentials = await getRelayCredentials();
  const normalizedSql = normalizeSql(sql);

  const response = await fetch(`${credentials.relay_url}/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': credentials.api_key,
    },
    body: JSON.stringify({ sql: normalizedSql }),
    signal: options?.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Query failed: ${response.status} ${text}`);
  }

  const data = (await response.json()) as RelayRawResponse;
  if (data.error) {
    throw new Error(data.error);
  }

  return {
    columns: data.columns ?? [],
    rows: data.rows ?? [],
    rowCount: data.rowCount ?? data.rows?.length ?? 0,
    trace_tree: data.trace_tree,
  };
}

export async function executeRelayDsl(
  dsl: string,
  options?: { signal?: AbortSignal },
): Promise<RelayQueryResult & { chart?: Record<string, unknown> }> {
  const credentials = await getRelayCredentials();
  const normalizedDsl = normalizeSql(dsl);

  const response = await fetch(`${credentials.relay_url}/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': credentials.api_key,
    },
    body: JSON.stringify({ sql: normalizedDsl }),
    signal: options?.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`DSL query failed: ${response.status} ${text}`);
  }

  const data = (await response.json()) as RelayRawResponse;
  if (data.error) {
    throw new Error(data.error);
  }

  return {
    columns: data.columns ?? [],
    rows: data.rows ?? [],
    rowCount: data.rowCount ?? data.rows?.length ?? 0,
    chart: data.chart,
    trace_tree: data.trace_tree,
    llm: data.llm,
  };
}

export async function executeRelayCode(code: string, options?: { signal?: AbortSignal }): Promise<RelayQueryResult> {
  return isDsl(code) ? executeRelayDsl(code, options) : executeRelayQuery(code, options);
}

/**
 * Execute DSL with .output() — writes data to catalog via relay /execute endpoint.
 * Unlike /query (read-only) and /materialize (caching only), /execute handles
 * arbitrary DSL including mock() and .output() for actual data writes.
 */
export async function executeRelayWrite(dsl: string, options?: { signal?: AbortSignal }): Promise<{ error?: string }> {
  const credentials = await getRelayCredentials();

  const response = await fetch(`${credentials.relay_url}/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': credentials.api_key,
    },
    body: JSON.stringify({ sql: dsl }),
    signal: options?.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    return { error: `Execute failed: ${response.status} ${text}` };
  }

  const data = (await response.json()) as RelayRawResponse;
  if (data.error) {
    return { error: data.error };
  }
  return {};
}
